/**
 * Lets `AgentAssistButton` (rendered by `@msys/ui`'s `Modal` inside its
 * header, i.e. OUTSIDE a `<Formik>` that sits within the modal's content)
 * read the dialog's current Formik values. Each dialog form drops an
 * `<AgentDialogFormValues />` reporter inside its `<Formik>`; the reporter
 * registers a getter keyed by its own DOM element, and the button looks up
 * the entries contained in ITS dialog (`closest('[role="dialog"]')`), so
 * stacked dialogs never see each other's values. The registry is ref-held:
 * no re-renders, values are read lazily on click.
 */
import { useFormikContext } from "formik";
import React from "react";

type Registry = Map<HTMLElement, () => Record<string, unknown>>;

const RegistryContext = React.createContext<Registry | undefined>(undefined);

export const AgentDialogFormValuesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const registryRef = React.useRef<Registry>(new Map());
  return (
    <RegistryContext.Provider value={registryRef.current}>
      {children}
    </RegistryContext.Provider>
  );
};

/** Place inside a dialog's `<Formik>`; renders nothing visible. No-op outside the provider. */
export function AgentDialogFormValues() {
  const registry = React.useContext(RegistryContext);
  const { values } = useFormikContext<Record<string, unknown>>();
  const latestValuesRef = React.useRef(values);
  latestValuesRef.current = values;
  const elementRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    const el = elementRef.current;
    if (!registry || !el) return undefined;
    registry.set(el, () => latestValuesRef.current);
    return () => {
      registry.delete(el);
    };
  }, [registry]);

  return <span hidden data-agent-form-values ref={elementRef} />;
}

/** Returns a reader: the merged values of all reporters inside the anchor's own dialog, or `undefined`. */
export function useDialogFormValues(): (
  anchor: HTMLElement | null
) => Record<string, unknown> | undefined {
  const registry = React.useContext(RegistryContext);
  return React.useCallback(
    anchor => {
      const dialog = anchor?.closest('[role="dialog"]');
      if (!registry || !dialog) return undefined;
      const entries = [...registry.entries()]
        .filter(([el]) => dialog.contains(el))
        .sort(([a], [b]) =>
          a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
            ? -1
            : 1
        );
      if (entries.length === 0) return undefined;
      return Object.assign({}, ...entries.map(([, get]) => get()));
    },
    [registry]
  );
}
